import { HttpClient } from '@angular/common/http';
import { inject, isDevMode } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { BrowserService } from '@ideals/services/browser';
import { ErrorLogger, LogLevel } from '@ideals/services/error-logger';
import { APP_CONFIG, LOCATION } from '@ideals/types';

import { EmailStorageService } from '../email-storage/email-storage.service';

export const IGNORED_ERROR_PATTERNS = ['/node_modules/'];

interface JsErrorLog {
  readonly Application: 'auth-frontend';
  readonly Browser: string;
  readonly ClientType: string;
  readonly Exception: string;
  readonly Level: LogLevel;
  readonly Message: string;
  readonly Os: string;
  readonly Url: string;
  readonly UserEmail?: string;
}

export class RemoteErrorLogger implements ErrorLogger {
  readonly #appConfig = inject(APP_CONFIG);
  readonly #browserService = inject(BrowserService);
  readonly #emailStorageService = inject(EmailStorageService);
  readonly #httpClient = inject(HttpClient);
  readonly #location = inject(LOCATION);

  available(): boolean {
    return !isDevMode();
  }

  async logHttpError(): Promise<void> {
    return Promise.resolve();
  }

  async logJsError(error: Error, exception: string, level: LogLevel): Promise<void> {
    if (IGNORED_ERROR_PATTERNS.some((pattern) => exception.includes(pattern))) {
      return;
    }

    const log = this.#getJsErrorLog(error, exception, level);

    await firstValueFrom(this.#httpClient.post(`${this.#appConfig.appUrl}/api/v1/log/public`, log));
  }

  #getJsErrorLog(error: Error, exception: string, level: LogLevel): JsErrorLog {
    const deviceInfo = this.#browserService.deviceInfo;

    return {
      Application: 'auth-frontend',
      Browser: `${deviceInfo.browser} ${deviceInfo.browser_version}`,
      ClientType: this.#browserService.platformType,
      Exception: exception,
      Level: level,
      Message: error.message,
      Os: `${deviceInfo.os} ${deviceInfo.os_version}`,
      Url: this.#location.href,
      UserEmail: this.#emailStorageService.get(),
    };
  }
}
